import React, {
  Fragment,
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import Select from "react-select";
import { Card, Col } from "reactstrap";
// import InputReturn from "./InputReturn";
import InputReturn from "./inputReturn";
import { Image, LI, UL } from "../../../AbstractElements";
import fetchFormApi from "./fetchFormApi";
import API from "../../../api/baseApi";
import sanitizeHtml from "../../../Utils/sanitizeHtml";

/**
 * Expected grid shape (example):
 * [
 *   {
 *     columns: [
 *       {
 *         input: {
 *           key: "firstName",
 *           label: "First Name",
 *           type: "text" | "number" | "textfield" | "select" | "radio" | "checkbox" | "date" | "time",
 *           placeholder: "Enter...",
 *           options: [{ label, value }],                // for select/radio
 *           checkboxOptions: [{ key: 'A' }, { key: 'B'}], // for checkbox group
 *           optionKey: "label",
 *           optionValue: "value",
 *           isCondition: true/false,
 *           conditionalRender: { key: "someFieldKey", value: true },
 *           required: true,
 *           validation: {
 *             minLength: 2, maxLength: 50, pattern: "^[A-Za-z ]+$",
 *             min: 0, max: 100,       // for number
 *             // for date/time you can also use min/max as ISO strings or Date objects
 *           }
 *         }
 *       }
 *     ]
 *   }
 * ]
 */

const PreviewForm = forwardRef(
  ({ grid: gridProps = [], type = "form", json = [] }, ref) => {
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [grid, setGrid] = useState(gridProps || []);
    // rowRepeatCounts[rowIndex] = number of extra copies (0 = just the original)
    const [rowRepeatCounts, setRowRepeatCounts] = useState({});

    // Keep local grid in sync with prop changes (e.g. when builder updates inputs)
    useEffect(() => {
      setGrid(Array.isArray(gridProps) ? gridProps : []);
    }, [gridProps]);

    // -------- Helpers ----------------------------------------------------------

    const getInputCfgByFieldKey = (fieldKey) => {
      // First try exact match, then try stripping _<number> suffix (Add More copies)
      const baseKey = fieldKey?.replace(/_\d+$/, '');
      for (const row of grid || []) {
        for (const col of row?.columns || []) {
          if (col?.input?.key === fieldKey || col?.input?.key === baseKey) return col?.input;
        }
      }
      return null;
    };

    const isColumnVisible = (column, keySuffix = '') => {
      const inp = column?.input;
      if (!inp?.isCondition) return true;
      const condKey = inp?.conditionalRender?.key;
      const condVal = inp?.conditionalRender?.value;
      if (!condKey || condVal === undefined || condVal === null || condVal === '') return true;
      const controllingField = getInputCfgByFieldKey(condKey);
      const fieldType = inp?.conditionalRender?.controllingType || controllingField?.type || '';

      // Normalize condKey: strip any stale _<number> suffix
      const baseCondKey = condKey.replace(/_\d+$/, '');

      // Helper: check if a single value matches the condition
      const matchesCondition = (val) => {
        if (val === undefined) return false;
        if (fieldType === 'checkbox') return !!val?.[condVal];
        if (fieldType === 'number') return val !== '' && Number(val) === Number(condVal);
        return String(val ?? '') === String(condVal);
      };

            if (keySuffix) {
        // Add More copy: first check the copy-specific controlling key (e.g. "field_2"),
        // then fall back to the exact condKey as stored (e.g. "field" or "field_1"),
        // and finally try any copy of the controlling field that satisfies the condition.
        const localKey = baseCondKey + keySuffix;
 
        // 1. Exact copy-specific key (e.g. "payment_type_2")
        if (formData?.[localKey] !== undefined) {
          return matchesCondition(formData[localKey]);
        }
        // 2. The exact condKey as defined (handles keys like "select_1" whose base "select" doesn't exist)
        if (formData?.[condKey] !== undefined) {
          return matchesCondition(formData[condKey]);
        }
        // 3. Stripped base key (e.g. "payment_type")
        if (formData?.[baseCondKey] !== undefined) {
          return matchesCondition(formData[baseCondKey]);
        }
        // 4. Any copy of the controlling field (same fallback as the no-suffix branch)
        const matchingKeys = Object.keys(formData).filter(
          (fKey) => fKey === condKey || fKey === baseCondKey || fKey.replace(/_\d+$/, '') === baseCondKey
        );
        if (matchingKeys.length > 0) {
          return matchingKeys.some((fKey) => matchesCondition(formData[fKey]));
        }
        return matchesCondition(undefined);
      }

      // No keySuffix: field is in a non-repeated row (or copy 1).
      // The controlling field may have Add More copies (TEST, TEST_2, ...).
      // Show this field if ANY copy of the controlling field matches.
      const matchingKeys = Object.keys(formData).filter(
        (fKey) => fKey === baseCondKey || fKey.replace(/_\d+$/, '') === baseCondKey
      );

      if (matchingKeys.length === 0) return matchesCondition(undefined);
      return matchingKeys.some((fKey) => matchesCondition(formData[fKey]));
    };

    const coerceDate = (d) => (d instanceof Date ? d : d ? new Date(d) : null);

    // Accepts RegExp or a pattern string + optional flags
    const normalizePattern = (pattern, flags) => {
      if (!pattern) return null;
      if (pattern instanceof RegExp) return pattern;
      if (typeof pattern === "string") {
        const fl = typeof flags === "string" ? flags : "";
        return new RegExp(pattern, fl);
      }
      return null;
    };

    // -------- Validation --------------------------------------------------------

    const validateField = (fieldKey, value, inputCfg) => {
      if (!inputCfg) return null;
      const { type, validation = {}, required, pattern } = inputCfg;

      // Required (generic)
      const isEmpty =
        value === null ||
        value === undefined ||
        value === "" ||
        (Array.isArray(value) && value.length === 0);

      if (required && type !== "checkbox") {
        if (isEmpty) return "This field is required.";
      }

      switch (type) {
        case "text":
        case "textfield": {
          const str = (value ?? "").toString();
          if (required && !str.trim()) return "This field is required.";
          if (validation.minLength && str.length < validation.minLength) {
            return `Minimum ${validation.minLength} characters required.`;
          }
          if (validation.maxLength && str.length > validation.maxLength) {
            return `Maximum ${validation.maxLength} characters allowed.`;
          }
          if (pattern) {
            const re =
              pattern instanceof RegExp ? pattern : new RegExp(pattern);
            if (!re.test(str)) return "Invalid format.";
          }
          break;
        }

        case "number": {
          if (value === "" || value === null || value === undefined) {
            if (required) return "This field is required.";
            break;
          }
          const num = Number(value);
          if (!Number.isFinite(num)) return "Please enter a valid number.";
          if (validation.min !== undefined && num < validation.min) {
            return `Minimum value is ${validation.min}.`;
          }
          if (validation.max !== undefined && num > validation.max) {
            return `Maximum value is ${validation.max}.`;
          }
          if (pattern) {
            const re =
              pattern instanceof RegExp ? pattern : new RegExp(pattern);
            if (!re.test(num)) return "Invalid format.";
          }
          break;
        }

        case "select": {
          if (
            required &&
            (value === "" || value === null || value === undefined)
          ) {
            return "Please select a value.";
          }
          break;
        }

        case "radio": {
          if (
            required &&
            (value === "" || value === null || value === undefined)
          ) {
            return "Please select an option.";
          }
          break;
        }

        case "checkbox": {
          // // Group-level validation: at least one option checked
          // const optKeys = inputCfg?.checkboxOptions?.map((o) => o.key) || [];
          // const anyChecked = optKeys.some((k) => !!formData[k]);
          // if (validation.required && !anyChecked) {
          //   return "Select at least one option.";
          // }
          // break;

          // Group-level validation under nested group object
          const groupObj = value || {}; // value is the nested object at formData[fieldKey]
          const optKeys = inputCfg?.checkboxOptions?.map((o) => o.key) || [];
          const anyChecked = optKeys.some((k) => !!groupObj[k]);
          if (required && !anyChecked) {
            return "Select at least one option.";
          }
          break;
        }

        case "file": {
          const emptyFileValue =
            value === null ||
            value === undefined ||
            value === "" ||
            (Array.isArray(value) && value.length === 0);
          if (required && emptyFileValue) {
            return "This field is required.";
          }
          break;
        }

        case "date":
        case "time": {
          if (!value) {
            if (required) return "This field is required.";
            break;
          }
          const dt = coerceDate(value);
          if (!(dt instanceof Date) || isNaN(dt.valueOf()))
            return "Invalid date/time.";
          if (validation.min) {
            const minDt = coerceDate(validation.min);
            if (minDt && dt < minDt)
              return `Must be on/after ${minDt.toLocaleString()}.`;
          }
          if (validation.max) {
            const maxDt = coerceDate(validation.max);
            if (maxDt && dt > maxDt)
              return `Must be on/before ${maxDt.toLocaleString()}.`;
          }
          if (pattern) {
            const re =
              pattern instanceof RegExp ? pattern : new RegExp(pattern);
            if (!re.test(value)) return "Invalid format.";
          }
          break;
        }

        default:
          break;
      }

      // Custom validator: function(value, formData) => string | null
      if (typeof validation.validate === "function") {
        const msg = validation.validate(value, formData);
        if (typeof msg === "string" && msg) return msg;
      }

      return null;
    };

    const validateForm = () => {
      const nextErrors = {};
      (grid || []).forEach((row, rowIndex) => {
        // How many total copies of this row exist (1 original + extras)
        const repeatCount = (rowRepeatCounts[rowIndex] || 0) + 1;
        for (let copy = 1; copy <= repeatCount; copy++) {
          const keySuffix = copy === 1 ? '' : `_${copy}`;
          (row?.columns || []).forEach((column) => {
            const inp = column?.input;
            if (!inp) return;
            if (!isColumnVisible(column, keySuffix)) return;

            const fieldKey = copy === 1 ? inp.key : `${inp.key}_${copy}`;
            const value = formData[fieldKey];
            const err = validateField(fieldKey, value, inp);
            if (err) nextErrors[fieldKey] = err;
          });
        }
      });
      setErrors(nextErrors);
      return { isValid: Object.keys(nextErrors).length === 0, nextErrors };
    };

    // -------- Input change handler ---------------------------------------------

    const handleInput = (eventOrValue, fieldKey, fieldType, extra = {}) => {
      setFormData((prev) => {
        const next = { ...prev };

        if (fieldType === "date" || fieldType === "time") {
          next[fieldKey] = eventOrValue ?? null;
        } else if (fieldType === "checkbox") {
          // ...existing code...
          const optionKey = extra.optionKey;
          const checked = !!eventOrValue?.target?.checked;
          next[fieldKey] = {
            ...(prev[fieldKey] || {}),
            [optionKey]: checked,
          };
        } else if (fieldType === "number") {
          const v = eventOrValue?.target?.value;
          next[fieldKey] =
            v === "" ? "" : Number.isFinite(Number(v)) ? Number(v) : "";
        } else if (fieldType === "radio") {
          next[fieldKey] = eventOrValue?.target?.value ?? "";
        } else if (fieldType === "file") {
          // File input: store File object or array of files
          const files = eventOrValue?.target?.files;
          if (files && files.length > 0) {
            next[fieldKey] = files.length === 1 ? files[0] : Array.from(files);
          } else {
            next[fieldKey] = null;
          }
        } else {
          next[fieldKey] = eventOrValue?.target?.value ?? "";
        }

        // Live-validate this field using updated state
        const inputCfg = getInputCfgByFieldKey(fieldKey);
        const err = validateField(fieldKey, next[fieldKey], inputCfg);
        setErrors((prevErr) => {
          const e = { ...prevErr };
          if (err) e[fieldKey] = err;
          else delete e[fieldKey];
          return e;
        });

        return next;
      });
    };

    // Expose imperative API for parent submit
    useImperativeHandle(ref, () => ({
      validate: () => validateForm(),
      getValues: () => {
        // Build a grouped structure: for rows with enableAddMore,
        // return an array of objects (one per copy). Other fields flat.
        const result = { ...formData };
        (grid || []).forEach((row, rowIndex) => {
          if (!row.enableAddMore) return;
          const repeatCount = (rowRepeatCounts[rowIndex] || 0) + 1;
          const keys = (row.columns || []).map((c) => c?.input?.key).filter(Boolean);
          if (!keys.length) return;
          // group key: derived from the row's field keys
          const groupKey = keys.join("_and_");
          result[groupKey] = [];
          for (let copy = 1; copy <= repeatCount; copy++) {
            const entry = {};
            keys.forEach((k) => {
              const fk = copy === 1 ? k : `${k}_${copy}`;
              entry[k] = formData[fk] ?? "";
            });
            result[groupKey].push(entry);
          }
        });
        return result;
      },
      reset: () => {
        setFormData({});
        setErrors({});
        setRowRepeatCounts({});
      },
    }));

    /****API Call For Select - fetch and populate options safely (supports pagination) */
    const fetchSelectAPI = async (
      rowIndex,
      columnIndex,
      apiData,
      page = 1,
      limit = 500,
      search = "",
    ) => {
      // mode: 'api' | 'schema' determines which backend to call
      const mode = apiData?._mode || (apiData?.schemaTable ? "schema" : "api");
      if (!apiData) return;

      try {
        if (mode === "schema") {
          const payload = {
            tableName: apiData.schemaTable,
            value: apiData.schemaValue,
            label: apiData.schemaLabel,
            page,
            limit,
            ...(search ? { search } : {}),
            // cascade filter: sent when a parent field is configured
            ...(apiData.ifFilterKey && apiData.ifFilterValue !== undefined && apiData.ifFilterValue !== ""
              ? { filterKey: apiData.ifFilterKey, filterValue: String(apiData.ifFilterValue) }
              : {}),
          };
          if (!payload.tableName || !payload.value) return;
          if (!payload.label) payload.label = payload.value;

          // mark loading state for this cell
          setGrid((prev) =>
            prev.map((r, ri) => {
              if (ri !== rowIndex) return r;
              return {
                columns: r.columns.map((c, ci) => {
                  if (ci !== columnIndex) return c;
                  if (!c.input) return c;
                  return {
                    ...c,
                    input: {
                      ...c.input,
                      _schemaPreviewLoading: true,
                    },
                  };
                }),
              };
            }),
          );

          const res = await API.post("/form/getDataByTable", payload);
          const maybe = res?.data;
          const data = Array.isArray(maybe)
            ? maybe
            : Array.isArray(maybe?.items)
            ? maybe.items
            : Array.isArray(maybe?.data)
            ? maybe.data
            : [];
          const mapped = Array.isArray(data)
            ? data.map((it) => ({
                label:
                  it.label ?? String(it?.[payload.label] ?? it?.[payload.value] ?? ""),
                value:
                  it.value ?? String(it?.[payload.value] ?? it?.[payload.label] ?? ""),
                ...it,
              }))
            : [];

          // append or set depending on page
          setGrid((prev) =>
            prev.map((r, ri) => {
              if (ri !== rowIndex) return r;
              return {
                columns: r.columns.map((c, ci) => {
                  if (ci !== columnIndex) return c;
                  if (!c.input) return c;
                  const prevItems = Array.isArray(c.input.options)
                    ? c.input.options
                    : [];
                  const nextItems = page === 1 ? mapped : [...prevItems, ...mapped];
                  const hasMore = mapped.length === limit; // heuristic
                  return {
                    ...c,
                    input: {
                      ...c.input,
                      options: nextItems,
                      _schemaPreviewFetched: true,
                      _schemaPreviewPage: page,
                      _schemaPreviewHasMore: hasMore,
                      _schemaPreviewLoading: false,
                    },
                  };
                }),
              };
            }),
          );
          // Persist the completed page in a ref so onFetchPage always reads
          // the latest value, avoiding stale-closure bugs.
          schemaCellPageRef.current[`${rowIndex}-${columnIndex}`] = page;
          return;
        }

        // default: api/cURL flow - support page/limit if fetchFormApi accepts it
        // attempt to pass page/limit on apiData for backends that honor them
        const apiPayload = { ...(apiData || {}), page, limit };
        const formatData = await fetchFormApi(apiPayload);
        const data = Array.isArray(formatData)
          ? formatData
          : Array.isArray(formatData?.items)
          ? formatData.items
          : Array.isArray(formatData?.data)
          ? formatData.data
          : [];

        setGrid((prev) =>
          prev.map((r, ri) => {
            if (ri !== rowIndex) return r;
            return {
              columns: r.columns.map((c, ci) => {
                if (ci !== columnIndex) return c;
                if (!c.input) return c;
                const prevItems = Array.isArray(c.input.options) ? c.input.options : [];
                const nextItems = page === 1 ? data : [...prevItems, ...data];
                const hasMore = data.length === limit;
                return {
                  ...c,
                  input: {
                    ...c.input,
                    options: nextItems,
                    _apiFetched: true,
                    _apiPreviewPage: page,
                    _apiPreviewHasMore: hasMore,
                    _apiPreviewLoading: false,
                  },
                };
              }),
            };
          }),
        );
      } catch (err) {
        // swallow errors to avoid render crashes
        setGrid((prev) =>
          prev.map((r, ri) => {
            if (ri !== rowIndex) return r;
            return {
              columns: r.columns.map((c, ci) => {
                if (ci !== columnIndex) return c;
                if (!c.input) return c;
                return {
                  ...c,
                  input: {
                    ...c.input,
                    _schemaPreviewLoading: false,
                    _apiPreviewLoading: false,
                  },
                };
              }),
            };
          }),
        );
      }
    };

    /**
     * Search handler for schema sources.
     * Resets the cell's options to [] and fires a fresh page-1 fetch with the search term.
     * Called with search="" to restore the full (unfiltered) option list.
     */
    const fetchSelectSearch = (rowIndex, columnIndex, inp, searchQuery) => {
      const cellKey = `${rowIndex}-${columnIndex}`;
      // Track the active search so the IntersectionObserver always uses it
      if (searchQuery) {
        searchQueryRef.current[cellKey] = searchQuery;
      } else {
        delete searchQueryRef.current[cellKey];
      }
      setGrid((prev) =>
        prev.map((r, ri) => {
          if (ri !== rowIndex) return r;
          return {
            columns: r.columns.map((c, ci) => {
              if (ci !== columnIndex) return c;
              if (!c.input) return c;
              return {
                ...c,
                input: {
                  ...c.input,
                  options: [],
                  _schemaPreviewFetched: false,
                  _schemaPreviewPage: 1,
                },
              };
            }),
          };
        }),
      );
      const apiPayload = { ...(inp.API || {}), _mode: "schema" };
      fetchSelectAPI(rowIndex, columnIndex, apiPayload, 1, 500, searchQuery);
    };

    // Trigger API fetches for any select inputs that require it (do not call during render)
    // We'll lazily fetch options when the input enters the viewport using IntersectionObserver.
    const fetchingRef = React.useRef(new Set());
    const nodeMapRef = React.useRef(new Map());
    // Track the last successfully loaded page per cell key "ri-ci".
    // Using a ref avoids stale-closure issues that occur when reading
    // inp._schemaPreviewPage from a render-time closure.
    const schemaCellPageRef = React.useRef({});
    // Track active search query per cell key "ri-ci" so IntersectionObserver
    // always includes the current search term instead of firing a bare fetch.
    const searchQueryRef = React.useRef({});
    // Always-current formData ref so the IntersectionObserver and cascade
    // re-fetch can read the latest parent-field values without closure issues.
    const formDataRef = React.useRef({});
    useEffect(() => {
      formDataRef.current = formData;
    }, [formData]);

    // Cascade re-fetch: when a parent field value changes, reset and re-fetch
    // any schema dropdowns that filter by that parent field (ifFilterEnable).
    const prevFilterFormDataRef = React.useRef({});
    useEffect(() => {
      const prev = prevFilterFormDataRef.current;
      const changedKeys = Object.keys(formData).filter((k) => formData[k] !== prev[k]);
      if (changedKeys.length > 0) {
        changedKeys.forEach((changedKey) => {
          const childKeysToReset = [];
          grid.forEach((r, ri) => {
            r.columns.forEach((c, ci) => {
              const inp = c?.input;
              if (!inp) return;
              if (!inp.ifFilterEnable || inp.previousFormKey !== changedKey || !inp.filterKey) return;

              // Reset the child field's value in formData for all source types
              if (inp.key) childKeysToReset.push(inp.key);

              const isSchema = inp.sourceType === "schema" || !!inp.isSchema || !!(inp.API && inp.API.useSchema);
              const isApi = !isSchema && (inp.sourceType === "api" || !!inp.isAPI);
              // JSON/manual options are filtered client-side in optionsToPass — nothing to re-fetch here
              if (!isSchema && !isApi) return;
              const parentVal = formData[changedKey];
              // Reset options so a fresh filtered fetch is triggered
              setGrid((prev2) =>
                prev2.map((r2, ri2) => {
                  if (ri2 !== ri) return r2;
                  return {
                    columns: r2.columns.map((c2, ci2) => {
                      if (ci2 !== ci) return c2;
                      if (!c2.input) return c2;
                      return {
                        ...c2,
                        input: {
                          ...c2.input,
                          options: [],
                          ...(isSchema
                            ? { _schemaPreviewFetched: false, _schemaPreviewPage: 1, _schemaPreviewHasMore: true }
                            : { _apiFetched: false, _apiPreviewPage: 1, _apiPreviewHasMore: true }),
                        },
                      };
                    }),
                  };
                }),
              );
              const mode = isSchema ? "schema" : "api";
              const apiPayload = {
                ...(inp.API || {}),
                _mode: mode,
                ...(parentVal !== undefined && parentVal !== null && parentVal !== ""
                  ? { ifFilterKey: inp.filterKey, ifFilterValue: parentVal }
                  : {}),
              };
              fetchSelectAPI(
                ri,
                ci,
                apiPayload,
                1,
                500,
                searchQueryRef.current[`${ri}-${ci}`] || "",
              );
            });
          });

          // Clear child field values after iterating to avoid mutating state mid-loop
          if (childKeysToReset.length > 0) {
            setFormData((prev) => {
              const next = { ...prev };
              childKeysToReset.forEach((k) => { next[k] = ""; });
              return next;
            });
            setErrors((prev) => {
              const next = { ...prev };
              childKeysToReset.forEach((k) => { delete next[k]; });
              return next;
            });
          }
        });

        // Clear values of fields that became conditionally hidden after parent changes.
        // This handles the case where e.g. unchecking "debit/credit_card" should wipe
        // Bank Name, Bank Contact Number, Card Number, Add More copies, etc.
        const condHiddenKeysToReset = [];
        // Row indices whose entire row became hidden — their Add More copies must be collapsed
        const rowIndicesToCollapse = new Set();
        changedKeys.forEach((changedKey) => {
          const baseChangedKey = changedKey.replace(/_\d+$/, '');
          grid.forEach((row, rowIndex) => {
            const repeatCount = (rowRepeatCounts[rowIndex] || 0) + 1;
            // If the row has enableAddMore and ALL its conditional columns are now hidden,
            // collapse the extra copies.
            if (row.enableAddMore && repeatCount > 1) {
              const condCols = (row.columns || []).filter(
                (col) => col?.input?.isCondition && col?.input?.conditionalRender?.key?.replace(/_\d+$/, '') === baseChangedKey,
              );
              if (condCols.length > 0 && condCols.every((col) => !isColumnVisible(col, ''))) {
                rowIndicesToCollapse.add(rowIndex);
                // Collect all extra-copy keys for this row so they are cleared too
                for (let copy = 2; copy <= repeatCount; copy++) {
                  (row.columns || []).forEach((col) => {
                    const k = col?.input?.key;
                    if (k) condHiddenKeysToReset.push(`${k}_${copy}`);
                  });
                }
              }
            }
            (row.columns || []).forEach((col) => {
              const inp = col?.input;
              if (!inp || !inp.isCondition) return;
              const condKey = inp.conditionalRender?.key;
              if (!condKey) return;
              const baseCondKey = condKey.replace(/_\d+$/, '');
              if (baseCondKey !== baseChangedKey) return;
              // Check every copy of this field (original + Add More)
              for (let copy = 1; copy <= repeatCount; copy++) {
                const keySuffix = copy === 1 ? '' : `_${copy}`;
                const fieldKey = copy === 1 ? inp.key : `${inp.key}_${copy}`;
                const val = formData[fieldKey];
                const isEmpty =
                  val === undefined ||
                  val === '' ||
                  val === null ||
                  (typeof val === 'object' &&
                    !Array.isArray(val) &&
                    Object.keys(val).length === 0);
                if (!isColumnVisible(col, keySuffix) && !isEmpty) {
                  condHiddenKeysToReset.push(fieldKey);
                }
              }
            });
          });
        });
        if (condHiddenKeysToReset.length > 0) {
          setFormData((prev) => {
            const next = { ...prev };
            condHiddenKeysToReset.forEach((k) => { next[k] = ''; });
            return next;
          });
          setErrors((prev) => {
            const next = { ...prev };
            condHiddenKeysToReset.forEach((k) => { delete next[k]; });
            return next;
          });
        }
        if (rowIndicesToCollapse.size > 0) {
          setRowRepeatCounts((prev) => {
            const next = { ...prev };
            rowIndicesToCollapse.forEach((ri) => { next[ri] = 0; });
            return next;
          });
        }
      }
      prevFilterFormDataRef.current = formData;
     
    }, [formData]);

    const setNode = (ri, ci, el) => {
      const key = `${ri}-${ci}`;
      if (el) {
        el.dataset.cellKey = key;
        nodeMapRef.current.set(key, el);
      } else {
        nodeMapRef.current.delete(key);
      }
    };

    useEffect(() => {
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const el = entry.target;
            const key = el.dataset?.cellKey;
            if (!key) return;
            const [riStr, ciStr] = key.split('-');
            const ri = Number(riStr);
            const ci = Number(ciStr);
            const cell = grid?.[ri]?.columns?.[ci];
            const inp = cell?.input;
            if (!inp) return;

            const isApiSource = !!inp.isAPI || inp.sourceType === 'api';
            const isJsonSource = !!inp.isJson || inp.sourceType === 'json';
            const isSchemaSource = !!inp.isSchema || inp.sourceType === 'schema' || inp.API?.useSchema;

            let mode = null;
            if (isApiSource) mode = 'api';
            else if (isJsonSource) mode = 'json';
            else if (isSchemaSource) mode = 'schema';
            if (!mode || mode === 'json') return;

            const fetchKey = `${ri}-${ci}-${mode}`;
            if (fetchingRef.current.has(fetchKey)) return;
            if ((mode === 'api' && inp._apiFetched) || (mode === 'schema' && inp._schemaPreviewFetched)) return;

            fetchingRef.current.add(fetchKey);
            const activeSearch = searchQueryRef.current[`${ri}-${ci}`] || "";
            // Build API payload; include server-side filter if ifFilterEnable is set
            let apiDataForFetch = { ...(inp.API || {}), _mode: mode };
            if (inp.ifFilterEnable && inp.previousFormKey && inp.filterKey) {
              const parentVal = formDataRef.current[inp.previousFormKey];
              if (parentVal !== undefined && parentVal !== null && parentVal !== "") {
                apiDataForFetch.ifFilterKey = inp.filterKey;
                apiDataForFetch.ifFilterValue = parentVal;
              }
            }
            fetchSelectAPI(ri, ci, apiDataForFetch, 1, 500, activeSearch).finally(() => {
              fetchingRef.current.delete(fetchKey);
            });
          });
        },
        { root: null, rootMargin: '200px', threshold: 0.1 },
      );

      nodeMapRef.current.forEach((el) => obs.observe(el));

      return () => obs.disconnect();
    }, [grid]);

    // -------- Render ------------------------------------------------------------

    if (type === "json") {
      return (
        <Fragment>
          <Col sm="12" className="my-gallery row gallery-with-description">
            {json.map((card, index) => (
              <figure className="col-xl-3 col-sm-6" key={`json-${index}`}>
                <Card>
                  <div className="blog-box blog-grid product-box text-center learning-box">
                    <div className="product-img">
                      <Image
                        attrImage={{
                          className: "img-fluid top-radius-blog",
                          src: `${card.image}`,
                          alt: card?.title || "card",
                        }}
                      />
                      <div className="product-hover">
                        <UL attrUL={{ className: "flex-row" }}>
                          <LI>
                            <i className="icon-link" />
                          </LI>
                          <LI>
                            <i className="icon-import" />
                          </LI>
                        </UL>
                      </div>
                    </div>
                    <div className="blog-details-main">
                      <h2>{card.title}</h2>
                      <hr />
                      <h6 className="blog-bottom-details">
                        {card.description}
                      </h6>
                    </div>
                  </div>
                </Card>
              </figure>
            ))}
          </Col>
        </Fragment>
      );
    }

    // type === "form"
    // File type dropdown options (all common types)
    const ALL_FILE_TYPE_OPTIONS = [
      { label: "All Files", value: "*/*" },
      { label: "Images", value: "image/*" },
      { label: "PDF", value: "application/pdf" },
      { label: "Word Document", value: "application/msword" },
      { label: "Excel Spreadsheet", value: "application/vnd.ms-excel" },
      { label: "PowerPoint", value: "application/vnd.ms-powerpoint" },
      { label: "Text File", value: "text/plain" },
      { label: "CSV", value: "text/csv" },
      { label: "ZIP", value: "application/zip" },
      { label: "Audio", value: "audio/*" },
      { label: "Video", value: "video/*" },
      { label: "JSON", value: "application/json" },
      { label: "SVG", value: "image/svg+xml" },
      { label: "PNG", value: "image/png" },
      { label: "JPG/JPEG", value: "image/jpeg" },
      { label: "GIF", value: "image/gif" },
      { label: "MP3", value: "audio/mpeg" },
      { label: "MP4", value: "video/mp4" },
      { label: "XML", value: "application/xml" },
    ];

    // Component to render a single file preview (image thumbnail or file info)
    const FilePreviewItem = ({ file }) => {
      const [previewUrl, setPreviewUrl] = useState(null);

      useEffect(() => {
        if (!file) {
          setPreviewUrl(null);
          return;
        }
        if (typeof file === "string") {
          setPreviewUrl(file);
          return;
        }
        try {
          const objUrl = URL.createObjectURL(file);
          setPreviewUrl(objUrl);
          return () => URL.revokeObjectURL(objUrl);
        } catch (e) {
          setPreviewUrl(null);
        }
      }, [file]);

      const name =
        file?.name ||
        (typeof file === "string" ? file.split("/").pop() : "file");
      const size = file?.size ? `${(file.size / 1024).toFixed(1)} KB` : "";
      const type = file?.type || "";
      const isImage =
        type.startsWith("image/") || /\.(png|jpe?g|gif|svg)$/i.test(name || "");

      return (
        <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
          {isImage ? (
            <div
              style={{
                width: 64,
                height: 64,
                overflow: "hidden",
                borderRadius: 4,
                flex: "0 0 64px",
              }}
            >
              <img
                src={previewUrl}
                alt={name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
          ) : (
            <div
              style={{
                width: 64,
                height: 64,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #dee2e6",
                borderRadius: 4,
                background: "#fff",
                flex: "0 0 64px",
              }}
            >
              <i
                className="fa fa-file"
                style={{ fontSize: 22, color: "#6c757d" }}
              />
            </div>
          )}
          <div className="file-meta">
            <div style={{ fontSize: 14, color: "#495057" }}>{name}</div>
            <div style={{ fontSize: 12, color: "#6c757d" }}>
              {type} {size}
            </div>
          </div>
        </div>
      );
    };

    // Render a single copy of a row's columns, with keySuffix="" for copy 1, "_2", "_3"...
    const renderRowColumns = (row, rowIndex, copy) => {
      const keySuffix = copy === 1 ? "" : `_${copy}`;
      return row?.columns?.map((column, columnIndex) => {
        const inp = column?.input;
        if (!inp) return null;
        if (!isColumnVisible(column, keySuffix)) return null;

        // Inline note/preview field (type === 'note'): render only HTML, skip input machinery
        if (inp.type === "note") {
          return (
            <div
              ref={(el) => setNode(rowIndex, columnIndex, el)}
              className="col"
              id={`grid_${rowIndex}_${columnIndex}_${copy}`}
              key={`col-note-${rowIndex}-${columnIndex}-${copy}`}
            >
              <div
                className="note-html"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(inp.noteHtml) }}
                style={{ fontSize: 13, color: "#495057" }}
              />
            </div>
          );
        }

        const baseKey = inp.key;
        const fieldKey = baseKey + keySuffix;

        // Compute options (unchanged logic, just use fieldKey)
        const optionsToPass = (() => {
          let opts = [];
          const isApiOrSchema =
            !!(inp.isAPI || inp.sourceType === "api") ||
            !!(inp.isSchema || inp.sourceType === "schema" || (inp.API && inp.API.useSchema));
          const isJsonSource = !!inp.isJson || inp.sourceType === "json" || inp.sourceType === "jsonText";

          if (isJsonSource) {
            const uploaded = Array.isArray(inp.uploadedJson) ? inp.uploadedJson : [];
            if (uploaded.length) {
              const labelKey = inp.jsonLabel || (inp.API && inp.API.label) || inp.optionKey || "label";
              const valueKey = inp.jsonValue || (inp.API && inp.API.value) || inp.optionValue || "value";
              opts = uploaded.map((item) => ({
                label: item && item[labelKey] !== undefined ? String(item[labelKey]) : String(item),
                value: item && item[valueKey] !== undefined ? String(item[valueKey]) : String(item),
                ...item,
              }));
            } else if (Array.isArray(inp.options) && inp.options.length) {
              opts = inp.options;
            }
          } else if (inp.type === "radio" && Array.isArray(inp.radioOptions) && inp.radioOptions.length) {
            opts = inp.radioOptions
              .filter((o) => o.label || o.key)
              .map((o) => ({ label: o.label, value: o.key }));
          } else if (Array.isArray(inp.options) && inp.options.length) {
            opts = inp.options;
          }

          if (!isApiOrSchema && inp.ifFilterEnable && inp.previousFormKey && inp.filterKey) {
            const parentVal = formData[inp.previousFormKey + keySuffix] ?? formData[inp.previousFormKey];
            if (parentVal !== undefined && parentVal !== null && parentVal !== "") {
              opts = opts.filter(
                (opt) => String(opt[inp.filterKey] ?? "") === String(parentVal),
              );
            }
          }
          return opts;
        })();

        // File upload
        if (inp.type === "file") {
          const allowedTypes =
            inp.allowedFileTypes && inp.allowedFileTypes.length
              ? inp.allowedFileTypes
              : ["*/*"];
          const fileVal = formData[fieldKey];
          const acceptStr = allowedTypes.join(",");

          return (
            <div
              className="col d-flex flex-column align-items-start"
              id={`grid_${rowIndex}_${columnIndex}_${copy}`}
              key={`col-${rowIndex}-${columnIndex}-${fieldKey}`}
              style={{ minWidth: 250, maxWidth: 350 }}
            >
              <label className="form-label fw-bold mb-1" style={{ color: "#495057" }}>
                {inp.label || "Upload File"}
                {inp.required && <span style={{ color: "red", marginLeft: 2 }}>*</span>}
              </label>
              <input
                type="file"
                className="form-control mb-2"
                accept={acceptStr}
                onChange={(e) => handleInput(e, fieldKey, "file")}
                style={{ border: "1px solid #ced4da", borderRadius: 4, background: "#fff" }}
              />
              <div className="mb-2 w-100">
                <Select
                  isMulti
                  isDisabled
                  value={ALL_FILE_TYPE_OPTIONS.filter(
                    (opt) =>
                      allowedTypes.includes(opt.value) || allowedTypes.includes("*/*"),
                  )}
                  options={ALL_FILE_TYPE_OPTIONS}
                  classNamePrefix="react-select"
                  placeholder="Allowed file types"
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                  styles={{
                    control: (base) => ({ ...base, minHeight: 32, borderColor: "#ced4da", boxShadow: "none" }),
                    multiValue: (base) => ({ ...base, background: "#e9ecef", color: "#495057" }),
                    option: (base, state) => ({
                      ...base,
                      color: "#495057",
                      background: state.isFocused ? "#f8f9fa" : "#fff",
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                    menu: (base) => ({ ...base, zIndex: 99999 }),
                  }}
                />
              </div>
              {fileVal ? (
                <div
                  className="file-preview w-100 mt-1"
                  style={{ background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: 4, padding: 8 }}
                >
                  {Array.isArray(fileVal) ? (
                    fileVal.map((f, idx) => <FilePreviewItem key={idx} file={f} />)
                  ) : (
                    <FilePreviewItem file={fileVal} />
                  )}
                </div>
              ) : null}
              {errors[fieldKey] && (
                <div className="text-danger mt-1" style={{ fontSize: 13 }}>
                  {errors[fieldKey]}
                </div>
              )}
            </div>
          );
        }

        // Default (text, number, select, radio, checkbox, date, time...)
        const isSchemaSource = !!(inp.isSchema || inp.sourceType === "schema" || (inp.API && inp.API.useSchema));
        const isApiSource = !!(inp.isAPI || inp.sourceType === "api");
        const paginatedSource = isSchemaSource || isApiSource;
        const loading = paginatedSource ? !!(isSchemaSource ? inp._schemaPreviewLoading : inp._apiPreviewLoading) : false;
        const hasMore = paginatedSource ? !!(isSchemaSource ? inp._schemaPreviewHasMore : inp._apiPreviewHasMore) : false;
        const currentPage = paginatedSource ? (isSchemaSource ? (inp._schemaPreviewPage || 1) : (inp._apiPreviewPage || 1)) : 1;

        let effMin = inp?.validation?.min;
        let effMax = inp?.validation?.max;
        if (inp?.type === "date") {
          const today = new Date();
          const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          if (inp.isCurrent) {
            effMin = startOfDay;
            effMax = startOfDay;
          } else if (inp.isPast) {
            const days = Number(inp.pastDays ?? 0);
            effMax = startOfDay;
            if (days > 0) effMin = new Date(startOfDay.getTime() - days * 86400000);
            else effMin = undefined;
          } else if (inp.isFuture) {
            const days = Number(inp.futureDays ?? 0);
            effMin = startOfDay;
            if (days > 0) effMax = new Date(startOfDay.getTime() + days * 86400000);
            else effMax = undefined;
          }
        }

        const dateMin = inp?.type === "date" || inp?.type === "time" ? effMin : undefined;
        const dateMax = inp?.type === "date" || inp?.type === "time" ? effMax : undefined;
        const numberMin = inp?.type === "number" ? (inp?.validation?.min ?? undefined) : undefined;
        const numberMax = inp?.type === "number" ? (inp?.validation?.max ?? undefined) : undefined;

        const numberRangeOptions = (() => {
          if (inp?.type !== "number" || !inp?.isRangeDropdown) return null;
          const rMin = Number(inp.rangeMin ?? 0);
          const rMax = Number(inp.rangeMax ?? 10);
          if (!Number.isFinite(rMin) || !Number.isFinite(rMax) || rMin > rMax) return null;
          const count = rMax - rMin + 1;
          if (count > 1000) return null;
          return Array.from({ length: count }, (_, i) => rMin + i);
        })();

        return (
          <div
            ref={(el) => setNode(rowIndex, columnIndex, el)}
            className="col d-flex flex-column"
            id={`grid_${rowIndex}_${columnIndex}_${copy}`}
            key={`col-${rowIndex}-${columnIndex}-${fieldKey}`}
          >
            <InputReturn
              name={inp.label}
              type={inp.type}
              required={!!inp.required}
              fieldKey={fieldKey}
              validation={inp.validation || {}}
              handleInput={(evOrVal, extra) => handleInput(evOrVal, fieldKey, inp.type, extra)}
              placeholder={inp.placeholder}
              error={errors}
              data={
                inp.type === "date" || inp.type === "time"
                  ? formData[fieldKey] || null
                  : inp.type === "checkbox"
                  ? formData[fieldKey] || {}
                  : (formData[fieldKey] ?? "")
              }
              formData={formData}
              options={optionsToPass}
              checkboxOptions={inp.checkboxOptions || []}
              optionKey={inp.optionKey || "label"}
              optionValue={inp.optionValue || "value"}
              min={dateMin ?? numberMin}
              max={dateMax ?? numberMax}
              numberRangeOptions={numberRangeOptions}
              onBlurValidate={() => {
                const err = validateField(fieldKey, formData[fieldKey], inp);
                setErrors((prevErr) => {
                  const e = { ...prevErr };
                  if (err) e[fieldKey] = err;
                  else delete e[fieldKey];
                  return e;
                });
              }}
              onFetchPage={
                paginatedSource
                  ? () => {
                      const cellKey = `${rowIndex}-${columnIndex}`;
                      const curPage = isSchemaSource
                        ? (schemaCellPageRef.current[cellKey] || 1)
                        : (inp._apiPreviewPage || 1);
                      const nextPage = curPage + 1;
                      const apiPayload = { ...(inp.API || {}), _mode: isSchemaSource ? "schema" : "api" };
                      fetchSelectAPI(rowIndex, columnIndex, apiPayload, nextPage, 500);
                    }
                  : null
              }
              paginated={paginatedSource}
              hasMore={hasMore}
              loading={loading}
              currentPage={currentPage}
              searchable={inp.type === "select"}
              onSearch={
                isSchemaSource
                  ? (q) => fetchSelectSearch(rowIndex, columnIndex, inp, q)
                  : null
              }
            />
            {inp.isPreview && inp.noteHtml && (
              <div
                className="note-html mt-1"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(inp.noteHtml) }}
                style={{ fontSize: 13, color: "#495057" }}
              />
            )}
          </div>
        );
      });
    };

    return (
      <div className="container">
        {grid.map((row, rowIndex) => {
          const repeatCount = (rowRepeatCounts[rowIndex] || 0) + 1; // total copies
          return (
            <div key={`row-${rowIndex}`}>
              {Array.from({ length: repeatCount }, (_, copyIdx) => {
                const copy = copyIdx + 1; // 1-based
                return (
                  <div
                    className="row py-2 mt-2"
                    key={`row-${rowIndex}-copy-${copy}`}
                    style={
                      row.enableAddMore && copy > 1
                        ? { borderTop: "1px solid #dee2e6", position: "relative" }
                        : undefined
                    }
                  >
                    <div className="d-flex w-100 flex-wrap gap-4">
                      {renderRowColumns(row, rowIndex, copy)}
                    </div>
                    {/* Remove button for extra copies */}
                    {row.enableAddMore && copy > 1 && (
                      <div className="d-flex justify-content-end mt-1">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => {
                            // Clean up formData and errors for the removed copy
                            const removedCopy = (rowRepeatCounts[rowIndex] || 0) + 1;
                            const suffix = `_${removedCopy}`;
                            const keysToRemove = (row.columns || []).map((c) => c?.input?.key).filter(Boolean).map((k) => k + suffix);
                            if (keysToRemove.length) {
                              setFormData((prev) => {
                                const next = { ...prev };
                                keysToRemove.forEach((k) => delete next[k]);
                                return next;
                              });
                              setErrors((prev) => {
                                const next = { ...prev };
                                keysToRemove.forEach((k) => delete next[k]);
                                return next;
                              });
                            }
                            setRowRepeatCounts((prev) => ({
                              ...prev,
                              [rowIndex]: (prev[rowIndex] || 0) - 1,
                            }));
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Add More button — only shown for rows that have it enabled AND at least one column is visible */}
              {row.enableAddMore && (row.columns || []).some((col) => isColumnVisible(col, '')) && (
                <div className="d-flex mt-1 mb-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() =>
                      setRowRepeatCounts((prev) => ({
                        ...prev,
                        [rowIndex]: (prev[rowIndex] || 0) + 1,
                      }))
                    }
                  >
                    + Add More
                  </button>
                </div>
              )}
              {/* Note previews — rendered once per row, below Add More */}
              {(row.columns || []).some(
                (col) => col?.input?.isNote && col?.input?.noteHtml,
              ) && (
                <div className="d-flex w-100 flex-wrap gap-4 mt-1">
                  {(row.columns || []).map((column, colIdx) => {
                    const inp = column?.input;
                    if (!(inp?.isNote && inp?.noteHtml)) return null;
                    return (
                      <div
                        key={`note-${rowIndex}-${colIdx}`}
                        className="col note-html-preview"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(inp.noteHtml) }}
                        style={{ fontSize: 13, color: "#495057" }}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );

  },
);

export default PreviewForm;
